import { useState, useEffect, useCallback } from 'react';
import { favoriteService, FavoriteOrder } from '../../services/favoriteService';
import { useNotifications } from '../../context/NotificationContext';
import { Order } from '../../types/order';

export const useFavorites = (userId?: number | null) => {
  const [favorites, setFavorites] = useState<FavoriteOrder[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasInitiallyLoaded, setHasInitiallyLoaded] = useState(false);
  const { showSuccess, showError } = useNotifications();

  // Favori siparişleri yükle - useCallback ile optimize et
  const fetchFavorites = useCallback(async () => {
    if (!userId) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const favoriteOrders = await favoriteService.getUserFavorites(userId);
      setFavorites(favoriteOrders);
      setHasInitiallyLoaded(true);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Favori siparişler yüklenemedi';
      setError(errorMessage);
      console.error('Favori siparişler yükleme hatası:', err);
      setHasInitiallyLoaded(true); // Hata durumunda da yüklenmiş say
    } finally {
      setLoading(false);
    }
  }, [userId]); // Sadece userId'ye bağımlı

  // Favori sil - anlık güncelleme
  const removeFavorite = useCallback(
    async (orderId: number) => {
      if (!userId) {
        showError('Hata', 'Kullanıcı bilgisi bulunamadı');
        return;
      }

      try {
        await favoriteService.removeFromFavorites(userId, orderId);
        // Listeyi hemen güncelle (API çağrısı yapmadan)
        setFavorites(prev => prev.filter(fav => fav.order_id !== orderId));
        showSuccess('Başarılı', 'Sipariş favorilerden kaldırıldı');
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Favori silinemedi';
        showError('Hata', errorMessage);
      }
    },
    [userId, showSuccess, showError]
  );

  // Favori ekle - anlık güncelleme
  const addFavoriteToList = useCallback(
    (orderDetails: Order) => {
      if (!userId) {
        return;
      }

      // Order'ı FavoriteOrder formatına dönüştür
      const favoriteOrder: FavoriteOrder = {
        favorite_id: Date.now(), // Geçici ID
        user_id: userId,
        favorited_at: new Date().toISOString(),
        order_id: orderDetails.id,
        user_name: orderDetails.user_name,
        user_surname: orderDetails.user_surname,
        management: orderDetails.management,
        office: orderDetails.office,
        total_amount: orderDetails.total_amount,
        status: orderDetails.status,
        ticket_used: orderDetails.ticket_used,
        order_created_at: orderDetails.created_at,
        order_updated_at: orderDetails.updated_at,
        item_count: orderDetails.items?.length || 0,
        items:
          orderDetails.items
            ?.filter(item => item.product_id)
            .map(item => ({
              id: item.id ?? 0,
              product_id: item.product_id,
              name: item.name,
              quantity: item.quantity,
              price: item.price,
              size: item.size,
              strength: item.strength,
            })) || [],
      };

      // Listeye hemen ekle
      setFavorites(prev => [favoriteOrder, ...prev]);
    },
    [userId]
  );

  // Favori kaldır - anlık güncelleme
  const removeFavoriteFromList = useCallback((orderId: number) => {
    setFavorites(prev => prev.filter(fav => fav.order_id !== orderId));
  }, []);

  // Favoriler listesindeki kalp ikonuna tıklama (toggle)
  const toggleFavoriteInList = useCallback(
    async (orderId: number) => {
      if (!userId) {
        return;
      }

      try {
        await favoriteService.removeFromFavorites(userId, orderId);
        // Listeyi hemen güncelle
        setFavorites(prev => prev.filter(fav => fav.order_id !== orderId));
        showSuccess('Başarılı', 'Sipariş favorilerden kaldırıldı');
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Favori silinemedi';
        showError('Hata', errorMessage);
      }
    },
    [userId, showSuccess, showError]
  );

  // İlk yükleme - sadece userId değiştiğinde ve henüz yüklenmemişse
  useEffect(() => {
    if (userId && !hasInitiallyLoaded) {
      fetchFavorites();
    }
  }, [userId, hasInitiallyLoaded, fetchFavorites]);

  return {
    favorites,
    loading,
    error,
    fetchFavorites,
    removeFavorite,
    addFavoriteToList,
    removeFavoriteFromList,
    toggleFavoriteInList,
    favoritesCount: favorites.length,
    hasInitiallyLoaded,
  };
};
